/**
 * Mobile header with hamburger menu and drawer navigation
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';
import { Menu, Users, Upload, Sun, Moon, LogOut, BookOpen, UserCog } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback } from './ui/avatar';

export function MobileHeader() {
  const [open, setOpen] = useState(false);
  const { userEmail, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === '/projects') {
      return location.pathname === '/projects' || location.pathname.startsWith('/results');
    }
    return location.pathname === path;
  };

  const allNavItems = [
    { path: '/projects', label: 'Projects', icon: Users, adminOnly: true },
    { path: '/qvt-primer', label: 'QVT Primer', icon: BookOpen, adminOnly: false },
    { path: '/upload', label: 'Upload', icon: Upload, adminOnly: false },
    { path: '/users', label: 'Users', icon: UserCog, adminOnly: true },
  ];

  const navItems = allNavItems.filter((item) => !item.adminOnly || isAdmin);

  const getUserInitials = () => {
    if (!userEmail) {
      return '?';
    }
    return userEmail.charAt(0).toUpperCase();
  };

  const handleNavClick = () => {
    setOpen(false);
  };

  return (
    <>
      {/* Mobile Header Bar */}
      <header className="sticky top-0 z-40 w-full border-b bg-card">
        <div className="flex h-16 items-center justify-between px-4">
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
            <Menu className="h-6 w-6" />
          </Button>

          <div className="flex items-center gap-2">
            <img src="/images/logo.png" alt="Picture Health Logo" className="h-6 w-6" />
            <span className="font-semibold">Picture Health</span>
          </div>

          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
        </div>
      </header>

      {/* Mobile Drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <div className="flex flex-col h-full">
            {/* Header */}
            <SheetHeader className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src="/images/logo.png" alt="Picture Health Logo" className="h-8 w-8" />
                  <SheetTitle className="text-lg">Picture Health</SheetTitle>
                </div>
              </div>
            </SheetHeader>

            {/* Navigation */}
            <nav className="flex-1 p-4 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={handleNavClick}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* User Section */}
            <div className="p-4 border-t">
              <div className="space-y-3">
                {/* User Info */}
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{userEmail ?? 'Guest'}</p>
                  </div>
                </div>

                <Separator />

                {/* Theme Toggle */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleTheme}
                  className="w-full justify-start"
                  id="toggle-theme-mobile"
                >
                  {theme === 'dark' ? (
                    <>
                      <Sun className="h-4 w-4 mr-2" />
                      Switch to Light Mode
                    </>
                  ) : (
                    <>
                      <Moon className="h-4 w-4 mr-2" />
                      Switch to Dark Mode
                    </>
                  )}
                </Button>

                {/* Logout */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    logout();
                    setOpen(false);
                  }}
                  className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
